import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useChild } from '../contexts/ChildContext';
import { useDashboardSettings, DEFAULT_SECTIONS } from '../hooks/useDashboardSettings';
import { useReminders, DEFAULT_REMINDER_SETTINGS } from '../hooks/useReminders';
import { useNotificationHistory } from '../hooks/useNotificationHistory';
import { formatTimeAgo } from '../utils/dateUtils';
import { useFCMToken } from '../hooks/useFCMToken';
import useDarkMode from '../hooks/useDarkMode';
import ChildSelector from '../components/ui/ChildSelector';
import LogoutButton from '../components/auth/LogoutButton';

const CAREGIVER_ROLES = [
  { value: 'mum',     label: 'Mum',     emoji: '👩' },
  { value: 'dad',     label: 'Dad',     emoji: '👨' },
  { value: 'nan',     label: 'Nan',     emoji: '👵' },
  { value: 'grandad', label: 'Grandad', emoji: '👴' },
  { value: 'nanny',   label: 'Nanny',   emoji: '🧑‍🍼' },
  { value: 'partner', label: 'Partner', emoji: '🤝' },
  { value: 'carer',   label: 'Carer',   emoji: '💙' },
  { value: 'other',   label: 'Other',   emoji: '👤' },
];

const INTERVAL_OPTIONS = [
  { label: '1 hour',    value: 60 },
  { label: '1.5 hours', value: 90 },
  { label: '2 hours',   value: 120 },
  { label: '2.5 hours', value: 150 },
  { label: '3 hours',   value: 180 },
  { label: '4 hours',   value: 240 },
];

const REMINDER_META = {
  bottle:        { icon: '🍼', label: 'Bottle Feed Reminder' },
  breastfeeding: { icon: '🤱', label: 'Breastfeeding Reminder' },
  diaper:        { icon: '🧷', label: 'Nappy Check Reminder' },
};

function SectionHeading({ icon, title }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.5rem',
      marginBottom: '1rem', paddingBottom: '0.75rem',
      borderBottom: '1px solid var(--border-light)',
    }}>
      <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>{icon}</span>
      <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)' }}>{title}</h2>
    </div>
  );
}

function ToggleRow({ label, description, checked, onChange }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '0.625rem 0',
    }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-dark)' }}>{label}</div>
        {description && (
          <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '0.1rem' }}>{description}</div>
        )}
      </div>
      <div className="form-check form-switch mb-0">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          checked={checked}
          onChange={onChange}
          style={{ cursor: 'pointer' }}
        />
      </div>
    </div>
  );
}

function SaveConfirm({ saved }) {
  if (!saved) return null;
  return (
    <span style={{ fontSize: '0.85rem', color: 'var(--accent-green)', fontWeight: 600 }}>✓ Saved</span>
  );
}

function DocsLink({ children, hash = '' }) {
  return (
    <a
      href={`/docs${hash}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: 'var(--primary-dark)', fontSize: '0.82rem' }}
    >
      {children}
    </a>
  );
}

export default function SettingsPage() {
  const { user, profile, profileLoading, saveProfile } = useAuth();
  const { selectedChild } = useChild();
  const { sections, saveSettings: saveDashboardSettings, loading: sectionsLoading } =
    useDashboardSettings(user?.uid);
  const { settings: reminderSettings, saveSettings: saveReminderSettings, loading: remindersLoading } =
    useReminders(selectedChild?.id, {});
  const { history: notifHistory, loading: notifHistoryLoading } =
    useNotificationHistory(selectedChild?.id);
  // FCM handles permission + token registration; useReminders just manages the settings doc
  const { permission, requestPermission } = useFCMToken(user?.uid);
  const [isDark, toggleDark] = useDarkMode();

  // ── Profile ─────────────────────────────────────────────────────────────────
  const [profileName, setProfileName]   = useState('');
  const [profileRole, setProfileRole]   = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved,  setProfileSaved]  = useState(false);
  const [profileError,  setProfileError]  = useState('');

  // Populate once profile loads
  useEffect(() => {
    if (profile?.displayName) setProfileName(profile.displayName);
    if (profile?.role)        setProfileRole(profile.role);
  }, [profile]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!profileName.trim()) { setProfileError('Please enter your name.'); return; }
    setProfileSaving(true);
    setProfileError('');
    try {
      await saveProfile({
        displayName: profileName.trim(),
        role: profileRole,
        email: user?.email || '',
        setupAt: profile?.setupAt || new Date().toISOString(),
      });
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);
    } catch {
      setProfileError('Could not save — please try again.');
    } finally {
      setProfileSaving(false);
    }
  };

  // ── Notifications ────────────────────────────────────────────────────────────
  const [reminderDraft,   setReminderDraft]   = useState(null);
  const [reminderSaving,  setReminderSaving]  = useState(false);
  const [reminderSaved,   setReminderSaved]   = useState(false);

  useEffect(() => {
    if (!remindersLoading) {
      setReminderDraft({ ...DEFAULT_REMINDER_SETTINGS, ...reminderSettings });
    }
  }, [remindersLoading, reminderSettings]);

  const updateReminder = (key, field, value) =>
    setReminderDraft((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

  const handleSaveReminders = async () => {
    if (!reminderDraft) return;
    setReminderSaving(true);
    try {
      await saveReminderSettings(reminderDraft);
      setReminderSaved(true);
      setTimeout(() => setReminderSaved(false), 3000);
    } finally {
      setReminderSaving(false);
    }
  };

  // ── Dashboard sections ───────────────────────────────────────────────────────
  const [sectionDraft,  setSectionDraft]  = useState(null);
  const [sectionSaving, setSectionSaving] = useState(false);
  const [sectionSaved,  setSectionSaved]  = useState(false);

  useEffect(() => {
    if (!sectionsLoading) setSectionDraft(sections.map((s) => ({ ...s })));
  }, [sectionsLoading, sections]);

  const toggleSection = (id) =>
    setSectionDraft((prev) => prev.map((s) => s.id === id ? { ...s, visible: !s.visible } : s));

  const moveSection = (index, dir) =>
    setSectionDraft((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const handleSaveSections = async () => {
    if (!sectionDraft) return;
    setSectionSaving(true);
    try {
      await saveDashboardSettings(sectionDraft);
      setSectionSaved(true);
      setTimeout(() => setSectionSaved(false), 3000);
    } finally {
      setSectionSaving(false);
    }
  };

  // ── iOS PWA detection ────────────────────────────────────────────────────────
  // iOS Safari only supports web push for PWAs added to the Home Screen (iOS 16.4+).
  // In a regular browser tab, FCM is not supported and permission will always be
  // 'unsupported'. We detect this case to show a targeted prompt rather than the
  // generic "browser does not support notifications" message.
  const isIOSNonStandalone = useMemo(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS) return false;
    const isStandalone =
      ('standalone' in navigator && navigator.standalone) ||
      window.matchMedia('(display-mode: standalone)').matches;
    return !isStandalone;
  }, []);

  // ── Children modal ───────────────────────────────────────────────────────────
  const [childrenOpen, setChildrenOpen] = useState(false);

  return (
    <div className="dashboard-container">
      <div style={{ maxWidth: 680, margin: '0 auto' }}>

        {/* Page header */}
        <div className="dashboard-header">
          <h1 className="dashboard-header-title">Settings</h1>
          <p className="dashboard-subtitle">Manage your profile, preferences, and app settings</p>
        </div>

        {/* ── 1. Profile ─────────────────────────────────────────────────────── */}
        <div className="tracking-section" style={{ marginBottom: '1.25rem' }}>
          <SectionHeading icon="👤" title="Profile" />

          {profileLoading ? (
            <p style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>Loading…</p>
          ) : (
            <form onSubmit={handleSaveProfile}>
              <div className="mb-3">
                <label className="form-label" style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                  Your name
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={profileName}
                  onChange={(e) => { setProfileName(e.target.value); setProfileError(''); }}
                  placeholder="e.g. Sarah"
                  maxLength={40}
                />
              </div>

              <div className="mb-3">
                <label className="form-label" style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                  Your role{' '}
                  <span style={{ color: 'var(--text-light)', fontWeight: 400 }}>(optional)</span>
                </label>
                <div className="profile-role-grid">
                  {CAREGIVER_ROLES.map((r) => (
                    <button
                      key={r.value}
                      type="button"
                      className={`profile-role-btn${profileRole === r.value ? ' active' : ''}`}
                      onClick={() => setProfileRole(profileRole === r.value ? '' : r.value)}
                    >
                      <span className="profile-role-emoji" aria-hidden="true">{r.emoji}</span>
                      <span className="profile-role-label">{r.label}</span>
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: '0.78rem', color: 'var(--text-light)', marginTop: '0.4rem', marginBottom: 0 }}>
                  Your name and role appear on entries you log, so other caregivers know who did what.{' '}
                  <DocsLink>Learn about caregiver roles →</DocsLink>
                </p>
              </div>

              {user?.email && (
                <div className="mb-3">
                  <label className="form-label" style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    Email address
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    value={user.email}
                    disabled
                    style={{ background: 'var(--bg-light)', color: 'var(--text-light)' }}
                  />
                </div>
              )}

              {profileError && (
                <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem' }}>
                  {profileError}
                </div>
              )}

              <div className="d-flex align-items-center gap-2">
                <button type="submit" className="btn btn-primary btn-sm" disabled={profileSaving}>
                  {profileSaving ? 'Saving…' : 'Save profile'}
                </button>
                <SaveConfirm saved={profileSaved} />
              </div>
            </form>
          )}
        </div>

        {/* ── 2. Appearance ──────────────────────────────────────────────────── */}
        <div className="tracking-section" style={{ marginBottom: '1.25rem' }}>
          <SectionHeading icon="🎨" title="Appearance" />
          <ToggleRow
            label="Dark mode"
            description="Easier on the eyes during night feeds"
            checked={isDark}
            onChange={toggleDark}
          />
        </div>

        {/* ── 3. Notifications & Reminders ───────────────────────────────────── */}
        <div className="tracking-section" style={{ marginBottom: '1.25rem' }}>
          <SectionHeading icon="🔔" title="Notifications & Reminders" />

          {/* iOS Safari non-standalone: push not supported in browser tab */}
          {isIOSNonStandalone && permission !== 'granted' && (
            <div className="alert alert-info mb-3" style={{ fontSize: '0.85rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>
                📱 Add to Home Screen to enable notifications
              </div>
              <p style={{ margin: '0 0 0.5rem 0' }}>
                On iPhone and iPad, push notifications only work when Baby Tracker is
                installed as a Home Screen app.
              </p>
              <ol style={{ margin: 0, paddingLeft: '1.2rem', lineHeight: 1.6 }}>
                <li>Tap the <strong>Share</strong> button <span aria-hidden="true">⬆</span> in Safari</li>
                <li>Tap <strong>"Add to Home Screen"</strong></li>
                <li>Open the app from your Home Screen</li>
                <li>Come back to Settings and enable notifications</li>
              </ol>
            </div>
          )}

          {/* Permission status banner — hidden on iOS non-standalone (handled above) */}
          {!isIOSNonStandalone && permission === 'unsupported' && (
            <div className="alert alert-warning py-2 mb-3" style={{ fontSize: '0.85rem' }}>
              Your browser does not support push notifications.
            </div>
          )}
          {permission === 'denied' && (
            <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem' }}>
              Notifications are blocked. Click the lock icon in your browser's address bar,
              allow notifications for this site, then reload.
            </div>
          )}
          {!isIOSNonStandalone && permission === 'default' && (
            <div className="alert alert-info py-2 mb-3" style={{ fontSize: '0.85rem' }}>
              <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                <span>Allow notifications to receive reminders.</span>
                <button className="btn btn-sm btn-primary" onClick={requestPermission}>
                  Enable notifications
                </button>
              </div>
            </div>
          )}
          {permission === 'granted' && (
            <div className="alert alert-success py-2 mb-3" style={{ fontSize: '0.85rem' }}>
              Notifications enabled.
            </div>
          )}

          <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '1rem' }}>
            Reminders fire based on the time since the last logged event, even when the
            screen is off.{' '}
            <DocsLink>Learn about reminders →</DocsLink>
          </p>

          {reminderDraft && Object.entries(REMINDER_META).map(([key, { icon, label }]) => {
            const cfg = reminderDraft[key] || DEFAULT_REMINDER_SETTINGS[key];
            return (
              <div
                key={key}
                style={{
                  border: '1px solid var(--border-light)',
                  borderRadius: '10px',
                  padding: '0.9rem 1rem',
                  marginBottom: '0.75rem',
                  background: cfg.enabled
                    ? 'rgba(var(--primary-soft-rgb, 255,179,217), 0.08)'
                    : 'transparent',
                  transition: 'background 0.2s',
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    {icon} {label}
                  </span>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={cfg.enabled}
                      onChange={(e) => updateReminder(key, 'enabled', e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </div>
                {cfg.enabled && (
                  <div>
                    <label className="form-label" style={{ fontSize: '0.82rem', color: 'var(--text-light)' }}>
                      Remind me after
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={cfg.intervalMinutes}
                      onChange={(e) => updateReminder(key, 'intervalMinutes', parseInt(e.target.value, 10))}
                      disabled={permission !== 'granted'}
                    >
                      {INTERVAL_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    {permission !== 'granted' && (
                      <p style={{ fontSize: '0.78rem', color: 'var(--text-light)', marginTop: '0.3rem', marginBottom: 0 }}>
                        Enable notifications above to activate this reminder.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-primary btn-sm"
              onClick={handleSaveReminders}
              disabled={reminderSaving || !reminderDraft}
            >
              {reminderSaving ? 'Saving…' : 'Save reminders'}
            </button>
            <SaveConfirm saved={reminderSaved} />
          </div>

          {/* Recent notification history */}
          {!notifHistoryLoading && notifHistory.length > 0 && (
            <div style={{ marginTop: '1.25rem' }}>
              <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-light)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Recent reminders
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {notifHistory.map((entry) => {
                  const meta = REMINDER_META[entry.type];
                  return (
                    <li
                      key={entry.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.45rem 0',
                        borderBottom: '1px solid var(--border-light)',
                        fontSize: '0.85rem',
                      }}
                    >
                      <span>
                        {meta?.icon ?? '🔔'} {meta?.label ?? entry.title}
                      </span>
                      <span style={{ color: 'var(--text-light)', fontSize: '0.78rem' }}>
                        {formatTimeAgo(new Date(entry.firedAt))}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        {/* ── 4. Dashboard & Navigation ───────────────────────────────────────── */}
        <div className="tracking-section" style={{ marginBottom: '1.25rem' }}>
          <SectionHeading icon="📊" title="Dashboard & Navigation" />

          <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '0.875rem' }}>
            Choose which sections appear in the sidebar and bottom navigation. Use the arrows
            to reorder.{' '}
            <DocsLink>Learn more →</DocsLink>
          </p>

          {sectionDraft ? (
            <>
              {sectionDraft.map((section, index) => (
                <div
                  key={section.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.6rem',
                    padding: '0.55rem 0.75rem',
                    marginBottom: '0.4rem',
                    borderRadius: '10px',
                    border: '1px solid var(--border-light)',
                    background: section.visible ? 'var(--bg-white)' : 'var(--bg-light)',
                    opacity: section.visible ? 1 : 0.55,
                    transition: 'opacity 0.2s, background 0.2s',
                  }}
                >
                  {/* Reorder arrows */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
                    <button
                      onClick={() => moveSection(index, -1)}
                      disabled={index === 0}
                      style={{
                        width: 22, height: 22, padding: 0,
                        border: '1px solid var(--border-light)', borderRadius: 4,
                        background: index === 0 ? 'transparent' : 'var(--bg-white)',
                        cursor: index === 0 ? 'default' : 'pointer',
                        fontSize: '0.65rem',
                        color: index === 0 ? 'var(--border-light)' : 'var(--text-dark)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                      title="Move up"
                    >▲</button>
                    <button
                      onClick={() => moveSection(index, 1)}
                      disabled={index === sectionDraft.length - 1}
                      style={{
                        width: 22, height: 22, padding: 0,
                        border: '1px solid var(--border-light)', borderRadius: 4,
                        background: index === sectionDraft.length - 1 ? 'transparent' : 'var(--bg-white)',
                        cursor: index === sectionDraft.length - 1 ? 'default' : 'pointer',
                        fontSize: '0.65rem',
                        color: index === sectionDraft.length - 1 ? 'var(--border-light)' : 'var(--text-dark)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                      title="Move down"
                    >▼</button>
                  </div>

                  <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{section.icon}</span>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-dark)' }}>
                      {section.label}
                    </span>
                    {section.fullWidth && (
                      <span style={{
                        marginLeft: '0.4rem', fontSize: '0.7rem',
                        color: 'var(--text-light)', background: 'var(--bg-light)',
                        border: '1px solid var(--border-light)', borderRadius: 4,
                        padding: '0 0.35rem',
                      }}>
                        full width
                      </span>
                    )}
                    {section.dashboardOnly && (
                      <span style={{
                        marginLeft: '0.4rem', fontSize: '0.7rem',
                        color: 'var(--text-light)', background: 'var(--bg-light)',
                        border: '1px solid var(--border-light)', borderRadius: 4,
                        padding: '0 0.35rem',
                      }}>
                        dashboard only
                      </span>
                    )}
                  </div>

                  <div className="form-check form-switch mb-0" style={{ flexShrink: 0 }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={section.visible}
                      onChange={() => toggleSection(section.id)}
                      style={{ cursor: 'pointer' }}
                      title={section.visible ? 'Hide section' : 'Show section'}
                    />
                  </div>
                </div>
              ))}

              <div className="d-flex justify-content-between align-items-center gap-2 mt-3">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setSectionDraft(DEFAULT_SECTIONS.map((s) => ({ ...s })))}
                >
                  Reset to defaults
                </button>
                <div className="d-flex align-items-center gap-2">
                  <SaveConfirm saved={sectionSaved} />
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleSaveSections}
                    disabled={sectionSaving}
                  >
                    {sectionSaving ? 'Saving…' : 'Save layout'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <p style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>Loading…</p>
          )}
        </div>

        {/* ── 5. Children & Sharing ─────────────────────────────────────────── */}
        <div className="tracking-section" style={{ marginBottom: '1.25rem' }}>
          <SectionHeading icon="👶" title="Children & Sharing" />

          <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '1rem' }}>
            Add children, switch profiles, invite caregivers, and manage journal sharing
            links.{' '}
            <DocsLink>Learn about sharing →</DocsLink>
          </p>

          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => setChildrenOpen(true)}
          >
            👶 Manage children &amp; sharing
          </button>

          {/* Inline children modal */}
          {childrenOpen && (
            <div
              className="child-switcher-backdrop"
              onClick={(e) => { if (e.target === e.currentTarget) setChildrenOpen(false); }}
              role="dialog"
              aria-modal="true"
              aria-label="Manage children"
            >
              <div className="child-switcher-modal">
                <div className="child-switcher-modal-header">
                  <span className="child-switcher-modal-title">Children &amp; Sharing</span>
                  <button
                    className="child-switcher-modal-close"
                    onClick={() => setChildrenOpen(false)}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
                <div className="child-switcher-modal-body">
                  <ChildSelector onChildSelected={() => setChildrenOpen(false)} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── 6. Account ────────────────────────────────────────────────────── */}
        <div className="tracking-section" style={{ marginBottom: '2rem' }}>
          <SectionHeading icon="🔐" title="Account" />

          {user?.email && (
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', margin: '0 0 0.15rem 0' }}>
                Signed in as
              </p>
              <p style={{ fontWeight: 600, fontSize: '0.9rem', margin: 0 }}>{user.email}</p>
            </div>
          )}

          <LogoutButton />
        </div>

      </div>
    </div>
  );
}
